import { Box, Paper, Tab, Tabs, Typography } from '@mui/material';
import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import MonacoEditorStyles from './MonacoEditorStyles';
import TrainingDetailInfoField from './TrainingDetailView/TrainingDetailInfoField';
import TrainingDetailFileExplorer from './TrainingDetailView/TrainingDetailFileExplorer';
import TrainingDetailEditorArea from './TrainingDetailView/TrainingDetailEditorArea';
import TrainingDetailSidePanel from './TrainingDetailView/TrainingDetailSidePanel';
import { handleFileClick as handleFileClickUtil } from '../utils/fileHandlers';
import {
  extractFilledValues,
  handleEditorDidChange as handleEditorDidChangeUtil,
  handleEditorDidMount as handleEditorDidMountUtil,
  updateLineDecorations,
} from '../utils/editorHandlers';
import { handleLineClick as handleLineClickUtil } from '../utils/lineHandlers';
import {
  ResizeColumnHandle,
  useResizableThreeColumns,
} from '../utils/resizableThreeColumns';

/**
 * 훈련 상세보기 컴포넌트 (기본 모드)
 */
const TrainingDetailView = ({
  training,
  step = 1,
  onStepChange,
}) => {
  const [selectedProblem, setSelectedProblem] = useState({
    problemNo: training.problemId,
    vulFiles: training.vulFiles,
    secFiles: training.secFiles,
    scodeGuideImg: training.scodeGuideImg || null,
  });

  const [showHint, setShowHint] = useState(true);
  const [showAnswers, setShowAnswers] = useState(true);
  const [activeFileName, setActiveFileName] = useState(selectedProblem.vulFiles[0]?.fileName);
  const [activeFileStep, setActiveFileStep] = useState(step);
  const [selectedLines, setSelectedLines] = useState([]);
  const [modifiedCode, setModifiedCode] = useState([]);
  const [originalCode, setOriginalCode] = useState(selectedProblem.secFiles);
  const [fileContents, setFileContents] = useState({});

  const editorRef = useRef(null);
  const decorationsRef = useRef({});
  const activeFileNameRef = useRef(selectedProblem.vulFiles[0]?.fileName);
  const fileClickCounterRef = useRef(0);

  const currentHint = useMemo(
    () => (step === 1 ? training.vulDesc : training.secDesc),
    [step, training.vulDesc, training.secDesc]
  );
  const currentAnswers = useMemo(
    () => (step === 1 ? training.vulFiles : training.secFiles),
    [step, training.vulFiles, training.secFiles]
  );

  const activeFile = useMemo(() => {
    if (activeFileStep === 1) {
      return selectedProblem.vulFiles?.find((f) => f.fileName === activeFileName);
    }
    if (activeFileStep === 2) {
      return selectedProblem.secFiles?.find((f) => f.fileName === activeFileName);
    }
    return step === 1 ? selectedProblem.vulFiles?.[0] : selectedProblem.secFiles?.[0] || selectedProblem.vulFiles?.[0];
  }, [activeFileStep, activeFileName, selectedProblem, step]);

  const files = activeFileStep === 1 ? selectedProblem.vulFiles : selectedProblem.secFiles;

  const handleStepChange = (event, newValue) => {
    onStepChange?.(event, newValue);
    setActiveFileStep(newValue);
    // 단계 변경 시 해당 단계의 첫 번째 파일 자동 선택
    const firstFile = newValue === 1
      ? selectedProblem.vulFiles?.[0]?.fileName
      : selectedProblem.secFiles?.[0]?.fileName || selectedProblem.vulFiles?.[0]?.fileName;
    if (firstFile) setActiveFileName(firstFile);
  };

  const handleFileClick = useCallback(
    (fileName, fileStep) => {
      handleFileClickUtil(fileName, fileStep, {
        step,
        selectedProblem,
        activeFileStep,
        fileClickCounterRef,
        activeFileNameRef,
        setActiveFileName,
        setActiveFileStep,
        setFileContents,
      });
    },
    [step, selectedProblem, activeFileStep]
  );

  const updateLineDecorationsCallback = useCallback((fileName, lineNumbers) => {
    updateLineDecorations(fileName, lineNumbers, { editorRef, decorationsRef });
  }, []);

  const handleLineClick = useCallback(
    (fileName, lineNumber) => {
      handleLineClickUtil(fileName, lineNumber, {
        step,
        activeFileName,
        setSelectedLines,
        updateLineDecorations: updateLineDecorationsCallback,
      });
    },
    [step, activeFileName, updateLineDecorationsCallback]
  );

  useEffect(() => {
    activeFileNameRef.current = activeFileName;
  }, [activeFileName]);

  useEffect(() => {
    if (!editorRef.current || !activeFileName) return;
    if (activeFileStep === 1) {
      let lines = [];
      if (step === 1) {
        const fileData = selectedLines.find((item) => item.fileName === activeFileName);
        lines = fileData?.lineNumbers || [];
      } else if (step >= 2) {
        const file = selectedProblem.vulFiles?.find((item) => item.fileName === activeFileName);
        lines = file?.lineNums || [];
      }
      updateLineDecorationsCallback(activeFileName, lines);
    }
  }, [step, activeFileName, selectedLines, activeFileStep, selectedProblem, updateLineDecorationsCallback]);

  const handleEditorDidMount = useCallback(
    (editor, monaco) => {
      handleEditorDidMountUtil(editor, monaco, {
        editorRef,
        activeFileStep,
        activeFileNameRef,
        step,
        activeFileName,
        selectedLines,
        selectedProblem,
        updateLineDecorations: updateLineDecorationsCallback,
        handleLineClick,
        decorationsRef,
      });
    },
    [step, activeFileName, activeFileStep, selectedLines, selectedProblem, handleLineClick, updateLineDecorationsCallback]
  );

  const handleEditorDidChange = useCallback(
    (value, event) => {
      handleEditorDidChangeUtil(value, event, {
        activeFileStep,
        activeFileName,
        originalCode,
        setFileContents,
        setModifiedCode,
        extractFilledValues,
      });
    },
    [activeFileStep, activeFileName, originalCode]
  );

  const {
    containerRef: mainAreaRef,
    leftWidth: explorerWidth,
    rightWidth: hintWidth,
    startResizeLeft: startResizeExplorer,
    startResizeRight: startResizeHint,
    handleWidthPx,
    minLeft: minExplorerWidth,
    minRight: minHintWidth,
    minCenter: minCenterWidth,
  } = useResizableThreeColumns();

  return (
    <>
      <MonacoEditorStyles />

      {/* 기본 정보 */}
      <Paper sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          기본 정보
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <TrainingDetailInfoField label="보안약점유형" value={training?.category?.name} />
          <TrainingDetailInfoField label="보안약점" value={training?.category?.subCategory?.name} />
          <TrainingDetailInfoField label="난이도" value={training?.level} />
          <TrainingDetailInfoField label="언어" value={training?.language?.name} />
          <TrainingDetailInfoField label="태그" value={training?.tags?.join(', ')} />
        </Box>
      </Paper>

      {/* 단계 탭 */}
      <Box sx={{ mb: 2 }}>
        <Tabs value={activeFileStep} onChange={handleStepChange}>
          <Tab label="1단계" value={1} />
          <Tab label="2단계" value={2} />
        </Tabs>
      </Box>

      {/* 메인 영역: 왼쪽 | 중앙 | 오른쪽 */}
      <Box
        ref={mainAreaRef}
        sx={{
          display: 'flex',
          flex: 1,
          alignItems: 'stretch',
          minHeight: 0,
          width: '100%',
        }}
      >
        <TrainingDetailFileExplorer
          files={files}
          activeFileName={activeFileName}
          activeFileStep={activeFileStep}
          step={step}
          selectedLines={selectedLines}
          modifiedCode={modifiedCode}
          onFileClick={handleFileClick}
          explorerWidth={explorerWidth}
          minExplorerWidth={minExplorerWidth}
        />

        <ResizeColumnHandle
          ariaLabel="파일 탐색기와 에디터 너비 조절"
          onMouseDown={startResizeExplorer}
          handleWidthPx={handleWidthPx}
        />

        <TrainingDetailEditorArea
          isApproval={training.isApproval}
          problemId={selectedProblem.problemNo}
          activeFileName={activeFileName}
          activeFileStep={activeFileStep}
          step={step}
          selectedLines={selectedLines}
          modifiedCode={modifiedCode}
          fileContents={fileContents}
          activeFile={activeFile}
          onEditorMount={handleEditorDidMount}
          onEditorChange={handleEditorDidChange}
          minCenterWidth={minCenterWidth}
        />

        <ResizeColumnHandle
          ariaLabel="에디터와 힌트 패널 너비 조절"
          onMouseDown={startResizeHint}
          handleWidthPx={handleWidthPx}
        />

        <TrainingDetailSidePanel
          hint={currentHint}
          answers={currentAnswers}
          step={step}
          showHint={showHint}
          showAnswers={showAnswers}
          onHintToggle={() => setShowHint((s) => !s)}
          onAnswersToggle={() => setShowAnswers((s) => !s)}
          hintWidth={hintWidth}
          minHintWidth={minHintWidth}
        />
      </Box>
    </>
  );
};

export default TrainingDetailView;
